import { useNavigate } from 'react-router-dom';
import { AUTH_ROUTES } from '@/api/constants/app.url';
import { getSessionSnapshot, useClearSession } from '@/shared/hooks/use-session';
import {
    activateAccount, forgetAccount, forgetAllAccounts, readAccounts,
} from '@/shared/utils/account-book';
import { homePathFor } from '@/modules/accounts';

/**
 * Sign-out — the legacy app's server action equivalent. Clears the cookie,
 * drops every cached query (they were tenant-scoped), then routes to the
 * sign-in page.
 *
 * With several accounts signed in (topbar → Add account), this signs out of
 * the ACTIVE one only:
 *
 *   - its entry leaves the account book, so its token doesn't linger and it
 *     can't be switched back into without signing in again;
 *   - if another saved account remains, that one becomes active — landing on
 *     the sign-in page while still signed in elsewhere would be a lie;
 *   - `allAccounts` signs out of every one of them instead.
 *
 * Note: there's no API call required in dev mode — the session lives only
 * in the browser cookie. When Clerk-react lands, this hook will additionally
 * call Clerk's signOut() before the local cleanup.
 */
export function useSignOut() {
    const clearSession = useClearSession();
    const navigate = useNavigate();
    return (opts: { allAccounts?: boolean } = {}) => {
        const current = getSessionSnapshot();
        if (opts.allAccounts) {
            forgetAllAccounts();
        } else {
            if (current) forgetAccount(current.userId);
            const next = readAccounts()[0];
            if (next) {
                // Full reload for the same reason the switcher does one: the
                // cached queries in memory belong to the account being left.
                activateAccount(next);
                window.location.replace(homePathFor(next.session.role));
                return;
            }
        }
        clearSession();
        navigate(AUTH_ROUTES.SIGN_IN, { replace: true });
    };
}
